import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { Reset16 } from 'ui/icons/aurora/16px/Reset16';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { TILE_METRICS_STUB } from 'modules/workflows/constants';
import { selectIsJourneySettingsRefactorFeatureFlagOn } from 'core/userContext';
import { useSelector } from 'react-redux';
import { MediumDropdown } from './MediumDropdown';
import { DROPDOWNS_LIST, MAX_SELECTED } from './Metrics.constants';
import { getDefaultTileMetrics, getOptionsAndCategories } from './Metrics.utils';
import { SettingsTab } from '../SettingsTakeover.constants';
import { Description, Header, HeaderWrapper } from '../SettingsTakeover.styles';
import { useResetMetrics } from '../SettingsTakeoverForm.hooks';

const Wrapper = styled.div`
  background: ${colors.grey1};
  padding: ${spacing.base};
  border-radius: ${spacing.half};
`;

const ResetButton = styled(Button)`
  && {
    // some awful css in buttons sets the stroke, making the path look thick
    &,
    &:hover {
      svg:not(.shape) {
        path {
          stroke: inherit;
        }
      }
    }
  }
`;

export const WRAPPER_TEST_ID = 'settings-dropdowns';
export const RESET_BUTTON_TEST_ID = 'reset-button';

export type MetricsSettingsProps = {
  isDisabled?: boolean;
};

export const JourneyMetricsOld = ({ isDisabled = false }: MetricsSettingsProps) => {
  const { analyticsMetrics } = useMetricsContext();
  const { reset, getValues, setValue } = useFormContext();

  const { categories, options } = useMemo(
    () => getOptionsAndCategories(analyticsMetrics),
    [analyticsMetrics],
  );

  return (
    <>
      <HeaderWrapper>
        <Header>Metrics to display</Header>
        <Description>
          Select up to {MAX_SELECTED} metrics to display on each tile type.
          <ResetButton
            data-test={RESET_BUTTON_TEST_ID}
            variant={BUTTON_VARIANTS.GHOST}
            onClick={() => {
              /* 
                This is a hack for resetting the metrics field to default values.  When you set these values, the controlled input does not 
                rerender until we do a reset.
              */
              setValue(SettingsTab.Metrics, getDefaultTileMetrics(TILE_METRICS_STUB), {
                shouldDirty: true,
              });
              reset(
                {
                  ...getValues(),
                  [SettingsTab.Metrics]: getDefaultTileMetrics(TILE_METRICS_STUB),
                },
                {
                  keepDirtyValues: true,
                  keepDirty: true,
                },
              );
            }}
          >
            <Reset16 />
            <span>Reset to default</span>
          </ResetButton>
        </Description>
      </HeaderWrapper>
      <Wrapper data-test={WRAPPER_TEST_ID}>
        {DROPDOWNS_LIST.map(({ medium, actionName }) => (
          <MediumDropdown
            key={medium}
            medium={medium}
            options={options[medium]}
            categories={categories}
            name={`${SettingsTab.Metrics}.${actionName}`}
            isDisabled={isDisabled}
          />
        ))}
      </Wrapper>
    </>
  );
};

export const JourneyMetricsNew = ({ isDisabled = false }: MetricsSettingsProps) => {
  const { analyticsMetrics } = useMetricsContext();
  const { categories, options } = useMemo(
    () => getOptionsAndCategories(analyticsMetrics),
    [analyticsMetrics],
  );
  const { resetMetrics } = useResetMetrics();

  return (
    <>
      <HeaderWrapper>
        <Header>Metrics to display</Header>
        <Description>
          Select up to {MAX_SELECTED} metrics to display on each tile type.
          <ResetButton
            isDisabled={isDisabled}
            data-test={RESET_BUTTON_TEST_ID}
            variant={BUTTON_VARIANTS.GHOST}
            onClick={resetMetrics}
          >
            <Reset16 />
            <span>Reset to default</span>
          </ResetButton>
        </Description>
      </HeaderWrapper>
      <Wrapper data-test={WRAPPER_TEST_ID}>
        {DROPDOWNS_LIST.map(({ medium, actionName }) => (
          <MediumDropdown
            key={medium}
            medium={medium}
            options={options[medium]}
            categories={categories}
            name={`${SettingsTab.Metrics}.${actionName}`}
            isDisabled={isDisabled}
          />
        ))}
      </Wrapper>
    </>
  );
};

export const JourneyMetrics = (props: MetricsSettingsProps) => {
  const isJourneySettingsFeatureFlagEnabled = useSelector(
    selectIsJourneySettingsRefactorFeatureFlagOn,
  );
  const Conversions = isJourneySettingsFeatureFlagEnabled ? JourneyMetricsNew : JourneyMetricsOld;
  return <Conversions {...props} />;
};
